// Key handling of the encrypted offline copy (OFFLINE_ENCRYPTION).
//
// Online: GET /api/offline/key hands out this user's data key (DK); it is kept
// in memory only. Once per device the user confirms the account password
// (POST /api/offline/verify-password); the DK is then stored wrapped with a
// key derived from that password (odCrypto.wrapDataKey) — the only key
// material that ever touches the disk. Offline: the password unwraps it.
//
// `pwv` (hash of the server-side password hash) tells when the password
// changed → the user confirms again; the old wrap keeps working until then.
// `kid` identifies the server secret: a new secret means the stored data can
// no longer be decrypted → it is wiped and synced again.
import Dexie from "dexie";
import { fromB64, wrapDataKey, unwrapDataKey } from "./odCrypto.js";
import { setRawKey } from "./secure.js";

const wrapKey = (t) => `od.offlineKeyWrap.${t}`;
const kidKey = (t) => `od.offlineKid.${t}`;
const ls = {
  get(k) { try { return localStorage.getItem(k); } catch { return null; } },
  set(k, v) { try { localStorage.setItem(k, v); } catch { /* ignore */ } },
  del(k) { try { localStorage.removeItem(k); } catch { /* ignore */ } },
};

let rawKey = null;        // only while online-verified, for wrapping
let server = null;        // {kid, pwv} of the last key fetch
const listeners = new Set();
const notify = () => listeners.forEach((fn) => { try { fn(); } catch { /* ignore */ } });
export function subscribeKeyState(fn) { listeners.add(fn); return () => listeners.delete(fn); }

export function loadWrap(tenant) {
  try { return JSON.parse(ls.get(wrapKey(tenant)) || "null"); } catch { return null; }
}

// 'missing' | 'ok' | 'stale' (password changed since the wrap was made)
export function wrapState(tenant) {
  const w = loadWrap(tenant);
  if (!w) return "missing";
  if (server && (w.pwv !== server.pwv || w.kid !== server.kid)) return "stale";
  return "ok";
}

// Plain-mode leftovers of this tenant: deleted once encryption is on.
export async function dropPlainCopy(tenant) {
  try { await Dexie.delete(`onlineDokuOffline_${tenant}`); } catch { /* ignore */ }
  if (typeof caches === "undefined") return;
  for (const n of [`od-files-${tenant}`, `od-thumbs-${tenant}`, `od-assets-${tenant}`]) {
    try { await caches.delete(n); } catch { /* ignore */ }
  }
}

async function dropEncryptedCopy(tenant) {
  try { await Dexie.delete(`onlineDokuOfflineE_${tenant}`); } catch { /* ignore */ }
  if (typeof caches !== "undefined") {
    for (const n of [`odx-files-${tenant}`, `odx-thumbs-${tenant}`, `odx-assets-${tenant}`]) {
      try { await caches.delete(n); } catch { /* ignore */ }
    }
  }
  ls.del(wrapKey(tenant));
}

// → 'ok' | 'unavailable'. Call before anything opens the offline DB.
export async function fetchOnlineKey(tenant, base = "") {
  let body;
  try {
    const r = await fetch(`${base}/api/offline/key`, { credentials: "include", redirect: "manual", cache: "no-store" });
    if (!r.ok) return "unavailable";
    body = await r.json();
  } catch { return "unavailable"; }
  const storedKid = ls.get(kidKey(tenant));
  if (storedKid && storedKid !== body.kid) await dropEncryptedCopy(tenant);   // server secret changed
  ls.set(kidKey(tenant), body.kid);
  rawKey = fromB64(body.key);
  server = { kid: body.kid, pwv: body.pwv };
  await setRawKey(rawKey);
  notify();
  return "ok";
}

// Online, once per device: check the password on the server, then store the wrap.
// → 'ok' | 'wrong' | 'limited' | 'error'
export async function confirmPassword(tenant, password, base = "") {
  if (!rawKey || !server) return "error";
  let res;
  try {
    const r = await fetch(`${base}/api/offline/verify-password`, {
      method: "POST", credentials: "include", redirect: "manual", cache: "no-store",
      headers: { "Content-Type": "application/json" }, body: JSON.stringify({ password }),
    });
    if (r.status === 429) return "limited";
    if (!r.ok) return "error";
    res = await r.json();
  } catch { return "error"; }
  if (!res.ok) return "wrong";
  const wrap = await wrapDataKey(rawKey, password, { pwv: res.pwv, kid: server.kid });
  ls.set(wrapKey(tenant), JSON.stringify(wrap));
  server = { ...server, pwv: res.pwv };
  notify();
  return "ok";
}

// Offline: unwrap with the password. Wrong password → throws.
export async function unlockWithPassword(tenant, password) {
  const w = loadWrap(tenant);
  if (!w) throw new Error("no wrap");
  await setRawKey(await unwrapDataKey(w, password));
}

export function forgetWrap(tenant) { ls.del(wrapKey(tenant)); notify(); }
